/**
 * A real GameCodex/Atlas account, required to submit to the Weight Council.
 *
 * RPGreek is a parody — but the account behind it is not. This calls Atlas's
 * public accounts API directly from the browser (CORS is wide open there by
 * design, same as any other GameCodex-ecosystem site) rather than proxying
 * through a Netlify Function, so no server-side code here ever sees a
 * password. Session tokens live in localStorage; the access token is short-
 * lived (15 min) and refreshed on demand by whoever calls `authedFetch`.
 */

const ATLAS_BASE = 'https://atlas.gamecodex.com/api'
const STORAGE_KEY = 'rpgreek.session'

export interface Account {
  id: string
  email: string
  name: string | null
}

export interface Session {
  accessToken: string
  refreshToken: string
  expiresIn: number
  account: Account
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

function saveSession(session: Session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY)
}

async function parseAuthResponse(res: Response): Promise<Session> {
  const data = (await res.json().catch(() => null)) as (Session & { error?: string }) | null
  if (!res.ok || !data?.accessToken) {
    throw new Error(data?.error || 'The account service could not complete that request.')
  }
  saveSession(data);
  return data
}

export async function register(email: string, password: string, name?: string): Promise<Session> {
  const res = await fetch(`${ATLAS_BASE}/accounts/register`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password, name, utm_content: 'rpgreek' }),
  })
  return parseAuthResponse(res)
}

export async function login(email: string, password: string): Promise<Session> {
  const res = await fetch(`${ATLAS_BASE}/accounts/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  return parseAuthResponse(res)
}

export async function logout(): Promise<void> {
  const session = getSession()
  clearSession()
  if (!session) return
  try {
    await fetch(`${ATLAS_BASE}/accounts/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    })
  } catch {
    // Local session is already cleared; a failed revoke on the server just
    // means the token dies naturally at its 15-minute expiry instead.
  }
}

async function refresh(): Promise<Session | null> {
  const session = getSession()
  if (!session) return null
  try {
    const res = await fetch(`${ATLAS_BASE}/accounts/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    })
    if (!res.ok) {
      clearSession()
      return null
    }
    return await parseAuthResponse(res)
  } catch {
    return null
  }
}

/**
 * Fetch with the current session's bearer token attached, refreshing once and
 * retrying on a 401 before giving up. Returns the raw Response either way —
 * callers still need to check `.ok` themselves for a REAL rejection.
 */
export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  let session = getSession()
  const withAuth = (init: RequestInit, token: string): RequestInit => ({
    ...init,
    headers: { ...init.headers, authorization: `Bearer ${token}` },
  })

  if (!session) throw new Error('not_authenticated')

  let res = await fetch(input, withAuth(init, session.accessToken))
  if (res.status === 401) {
    session = await refresh()
    if (!session) throw new Error('not_authenticated')
    res = await fetch(input, withAuth(init, session.accessToken))
  }
  return res
}
